import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import OktaSignIn from '@okta/okta-signin-widget';
import { getOktaSignInConfig } from '@/config';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { shallow } from 'zustand/shallow';
import { Box, Divider, Grid, Typography } from '@mui/material';
import { OktaSignInWidget } from '@components/oktaSignInWidget/OktaSignInWidget';
import { PasswordResetAccountCheck } from '@components/oktaSignInWidget/PasswordResetAccountCheck';
import PageHead from '@utils/PageHead';
import logo from '@assets/svgs/logo.svg';
import SpecialtyLogo from '@assets/svgs/lsic-logo.svg';
import InsureonLogo from '@assets/logo/insureon.svg';

import { ToastType } from '@components/toast/ToastType.enum';
import { FullscreenLoading } from '@components/Loading';
import { useStore } from '@store/GlobalStore';

import { useCurrentUser } from '@services/user';

import { useToastStore } from '@store/Toast';
import { ApiEnvBanner } from '@/ApiEnvBanner';
import { Layout } from '@layout/Layout';
import React from 'react';

import {
  LoginView,
  LoginSource,
  RegistrationRole,
  getRegistrationPrefill,
  REGISTRATION_LINK_PARAMS,
} from './constants';
import { renderView } from '@pages/Login/renderView';
import { IndicationWelcomeMessage } from './WelcomeMessages/IndicationWelcomeMessage';
import { QuoteSetupPresetWelcomeMessage } from './WelcomeMessages/QuoteSetupPresetWelcomeMessage';
import { useGetIndicationSummary } from '@services/indications/useGetIndicationSummary';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { rememberRedirectUrl, sanitizeRedirectUrl } from '@utils/url';
import { INSUREON_EVENTS_PRESET_SLUG } from '@/constants/presetSlugs';

export function Login({ view }: { view?: LoginView }) {
  const { oktaAuth } = useOktaAuth();
  const { persistToast, clearPersistToast } = useToastStore();
  const [redirectUrl, setRedirectUrl] = React.useState<undefined | string>();

  const [forceDefaultWelcomeMessage, setForceDefaultWelcomeMessage] = useState(false);
  const [selectedRole, setSelectedRole] = React.useState<RegistrationRole | undefined>();
  const [isRoleSelectionRequested, setIsRoleSelectionRequested] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const urlSearchParams = new URLSearchParams(window.location.search);
  const magicLinkOtp = urlSearchParams.get('otp');
  const magicLinkState = urlSearchParams.get('state');
  const isMagicLink = !!(magicLinkOtp && magicLinkState);
  // An email's register link pre-fills sign-up. Held in state, so it survives removing the link's
  // details from the URL below. /login and /sign-up share this component, so the effect also takes a
  // new link's details while mounted, and drops the prefill when the view leaves sign-up.
  const [registrationPrefill, setRegistrationPrefill] = useState(() =>
    view === LoginView.SIGNUP ? getRegistrationPrefill(urlSearchParams) : undefined,
  );

  useEffect(() => {
    if (view !== LoginView.SIGNUP) {
      setRegistrationPrefill(undefined);
      return;
    }
    const params = new URLSearchParams(location.search);
    if (!REGISTRATION_LINK_PARAMS.some((key) => params.has(key))) return;
    setRegistrationPrefill(getRegistrationPrefill(params));
    // The link can also say where to land once the new user signs in, e.g. their message.
    const remembered = rememberRedirectUrl(params.get('redirect') ?? undefined);
    if (remembered) setRedirectUrl(remembered);
    // Keep the brokerage code, email and name out of the address bar and the browser history.
    REGISTRATION_LINK_PARAMS.forEach((key) => params.delete(key));
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
  }, [view, location.pathname, location.search, navigate]);

  useEffect(() => {
    const url = localStorage.getItem('redirectUrl');
    const redirectUrlExpiration = localStorage.getItem('redirectUrlExpiration');

    if (redirectUrlExpiration && new Date(redirectUrlExpiration) < new Date()) {
      // if expiration is in the past, remove the redirectUrl
      // this will persist for an hour - giving the user hopefully plenty time to login
      // this way if they 'refresh' or close the browser they don't lose the context of their login source
      localStorage.removeItem('redirectUrl');
      localStorage.removeItem('redirectUrlExpiration');
    } else if (url) {
      setRedirectUrl(sanitizeRedirectUrl(url));
    }
  }, []);

  let loginSource = LoginSource.LOGIN;
  // if redirectUrl and redirectUrl starts with /indication/:cuid then set loginSource to indication
  if (redirectUrl && redirectUrl.startsWith('/indication/')) {
    loginSource = LoginSource.INDICATION;
  }
  if (redirectUrl && redirectUrl.startsWith('/new-quote?preset=')) {
    loginSource = LoginSource.QUOTE_SETUP_PRESET;
  }

  const { isAuthenticated } = useStore((state) => state.userAuth, shallow);
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser, isPending: isCurrentUserPending } = useCurrentUser();
  const apiEnv = window.lio.getApiEnvironment();

  const indicationId = loginSource === LoginSource.INDICATION ? redirectUrl?.split('/')[2]?.split('?')[0] : undefined;

  let queryString = redirectUrl?.split('?')[1];
  let searchParams = new URLSearchParams(queryString);
  const source = searchParams.get('source') ?? undefined;
  const isApiPartnerIndication = loginSource === LoginSource.INDICATION && source === 'api-partner';

  const { data: indicationSummary } = useGetIndicationSummary(indicationId, isApiPartnerIndication);
  const goToSignUp = useCallback(() => {
    navigate('/sign-up');
  }, [navigate]);

  const oktaSignInConfig = useMemo(() => {
    return getOktaSignInConfig(
      apiEnv,
      indicationSummary?.createdByUser?.email,
      goToSignUp,
      magicLinkOtp,
      magicLinkState,
    );
  }, [apiEnv, indicationSummary, goToSignUp, magicLinkOtp, magicLinkState]);

  const widget = new OktaSignIn(oktaSignInConfig);

  const onSuccess = async (tokens: string) => {
    await oktaAuth.handleLoginRedirect(tokens);

    if (redirectUrl) {
      // clear if user was successfully logged in
      localStorage.removeItem('redirectUrl');
      localStorage.removeItem('redirectUrlExpiration');
      navigate(redirectUrl);
    }
  };

  useEffect(() => {
    return () => {
      clearPersistToast();
    };
    // eslint-disable-next-line
  }, [persistToast]);

  const onError = useCallback(
    (err: any) => {
      console.log('error logging in', err, {
        name: err.name,
        errorCode: err.errorCode,
        errorSummary: err.errorSummary,
        error: err.error,
        errorDescription: err.errorDescription,
      });

      if (
        err?.name === 'OAuthError' &&
        err?.errorCode === 'invalid_grant' &&
        err?.errorSummary === 'PKCE verification failed.'
      ) {
        if (redirectUrl) {
          navigate(redirectUrl);
        } else {
          navigate('/');
        }
      }

      if (err.errorCode === 'login_required') {
        console.log('No active session, login required');
      }
      if (err.errorCode === 'access_denied') {
        enqueueSnackbar('Application access missing', {
          variant: ToastType.Error,
          persist: true,
        });
      }
    },
    [enqueueSnackbar, navigate, redirectUrl],
  );

  useEffect(() => {
    if (isMagicLink) return;
    oktaAuth.token
      .getWithoutPrompt(oktaSignInConfig)
      .then((res: any) => {
        oktaAuth.tokenManager.setTokens(res.tokens);
      })
      .catch((e: any) => onError(e));
  }, [oktaAuth.token, oktaAuth.tokenManager, oktaSignInConfig, onError, isMagicLink]);

  const greeting = new Date().getHours() < 12 ? 'Good morning!' : 'Good afternoon!';
  const { isMobileScreen } = useGetScreenSize();

  useEffect(() => {
    if (indicationSummary && !indicationSummary?.createdByUser?.idpUserId && !location.state?.justRegistered) {
      navigate('/sign-up');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indicationSummary]);

  useEffect(() => {
    if (isAuthenticated && currentUser?.activatedAt && redirectUrl) {
      localStorage.removeItem('redirectUrl');
      localStorage.removeItem('redirectUrlExpiration');
    }
  }, [isAuthenticated, currentUser?.activatedAt, redirectUrl]);

  console.log('[Login] render state', {
    isAuthenticated,
    view,
    isCurrentUserPending,
    currentUserId: currentUser?.id,
    activatedAt: currentUser?.activatedAt,
    redirectUrl,
  });

  if (isAuthenticated) {
    if (isCurrentUserPending) {
      console.log('[Login] authenticated but currentUser still pending — showing loading');
      return <FullscreenLoading />;
    }
    if (redirectUrl) {
      console.log('[Login] authenticated, activatedAt set, redirectUrl present — navigating to', redirectUrl);
      return <Navigate to={{ pathname: redirectUrl }} />;
    }
    console.log('[Login] authenticated, activatedAt set — navigating to /');
    return <Navigate to={{ pathname: '/' }} />;
  }

  const storedLogo = localStorage.getItem('logo') ?? '';
  const showSpecialtyLogo = storedLogo === 'lsic-logo';

  return (
    <>
      <ApiEnvBanner />
      <Layout title='Login'>
        <PageHead title='Login' />
        <Grid
          container
          sx={{
            justifyContent: 'space-evenly',
            alignItems: 'center',
            alignContent: 'center',
            gap: { sm: 4, md: 'inherit' },
            backgroundColor: 'background.gradient',
            height: '100vh',
          }}
        >
          <Grid
            size={{ xs: 6, sm: 6, md: 4, lg: 4, xl: 4 }}
            style={{
              color: 'white',
            }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              alignContent: 'center',
              mt: !isMobileScreen && loginSource !== LoginSource.LOGIN ? '-30px' : 0,
            }}
          >
            {loginSource === LoginSource.QUOTE_SETUP_PRESET &&
            searchParams.get('preset')?.toLowerCase() === INSUREON_EVENTS_PRESET_SLUG ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                  gap: 2,
                }}
              >
                <Box component='img' src={logo} alt='LIO' sx={presetLogoImgSx} />
                <Divider
                  orientation='vertical'
                  flexItem
                  sx={{
                    borderColor: 'rgba(255,255,255,0.5)',
                    mx: 1,
                    height: 60,
                    alignSelf: 'center',
                  }}
                />
                <Box component='img' src={InsureonLogo} alt='Insureon' sx={presetLogoImgSx} />
              </Box>
            ) : (
              <img
                src={showSpecialtyLogo ? SpecialtyLogo : logo}
                alt={showSpecialtyLogo ? 'lsci-logo' : 'logo'}
                style={{ padding: '35px', maxHeight: 270, maxWidth: 270 }}
                // 270 is max size of LIO logo so restrict LSIC to the same
              />
            )}
            {!isMobileScreen ? (
              <Box>
                {!forceDefaultWelcomeMessage && loginSource === LoginSource.INDICATION ? (
                  <IndicationWelcomeMessage view={view} />
                ) : !forceDefaultWelcomeMessage && loginSource === LoginSource.QUOTE_SETUP_PRESET ? (
                  <QuoteSetupPresetWelcomeMessage
                    view={view}
                    redirectUrl={redirectUrl ?? ''}
                    onInvalidSlug={() => setForceDefaultWelcomeMessage(true)}
                  />
                ) : (
                  <Typography
                    variant='subtitle'
                    sx={{
                      marginBottom: '24px',
                      width: '235px',
                    }}
                  >
                    {greeting}
                  </Typography>
                )}
              </Box>
            ) : null}
          </Grid>
          <Grid
            size={{ xs: 12, sm: 'auto' }}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              '#okta-sign-in .auth-footer a': {
                marginRight: '12px',
              },
              '#okta-sign-in .custom-buttons': {
                '& .separation-line': {
                  marginTop: '4px',
                  fontWeight: 400,
                },
                '& a.btn-customAuth': {
                  fontSize: '14px',
                  '&:hover': {
                    color: '#fccd48',
                  },
                },
              },
              '#okta-sign-in .enroll-choices a.button.link-button.skip-all': {
                marginLeft: '42%',
              },
            }}
          >
            {renderView({
              view,
              loginSource,
              indicationSummary,
              isApiPartnerIndication,
              isRoleSelectionRequested,
              selectedRole,
              registrationPrefill,
              onRoleSelect: (role) => {
                setSelectedRole(role);
                setIsRoleSelectionRequested(true);
              },
              onSelectDifferentRole: () => {
                setSelectedRole(undefined);
                setIsRoleSelectionRequested(true);
              },
              redirectUrl,
            }) ?? (
              <>
                <OktaSignInWidget
                  config={oktaSignInConfig}
                  onSuccess={onSuccess}
                  onError={onError}
                  widget={widget}
                  preset={
                    loginSource === LoginSource.QUOTE_SETUP_PRESET
                      ? (searchParams.get('preset') ?? undefined)
                      : undefined
                  }
                />
                <PasswordResetAccountCheck widget={widget} />
              </>
            )}
          </Grid>
        </Grid>
      </Layout>
    </>
  );
}

const presetLogoImgSx = {
  objectFit: 'contain',
  width: { xs: '50%', sm: 120, md: 180, lg: 'unset' },
  height: { xs: '50%', sm: 120, md: 180, lg: 'unset' },
  maxHeight: 200,
  maxWidth: 200,
};
